'use client';
import { useCallback, useState } from 'react';

import { ConfirmModal, NSFWAgeConfirmation, useSwitchSafeMode } from '~features/nsfw';
import { useModal } from '~hooks/useModal';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { GTMAnalytics } from '~services/analytics';

import { AgeConfirmationContext } from './index';
import type { TAgeConfirmationConfig } from './types';

type Props = {
	children: React.ReactNode;
};

export const AgeConfirmationProvider = ({ children }: Props) => {
	const { isOpen, open, close } = useModal();
	// temporary, until purchases on joi.ai don't work
	const showCancelButton = false;
	// const [showCancelButton, setShowCancelButton] = useState(true);
	const { isDemoUser } = useSession();
	const t = useTranslations('AgeConfirmationModal');
	const switchSafeMode = useSwitchSafeMode();
	const [modalConfig, setModalConfig] = useState<TAgeConfirmationConfig>({} as TAgeConfirmationConfig);

	const openAgeConfirmation = useCallback(
		(config: TAgeConfirmationConfig) => {
			setModalConfig(config);
			open()();
		},
		[open]
	);

	const handleConfirm = useCallback(() => {
		// setShowCancelButton(true);
		void switchSafeMode(false, { nsfwReason: modalConfig.nsfwReason })
			.then((value) => {
				modalConfig.resolve?.(value);
				setModalConfig({} as TAgeConfirmationConfig);
			});

		if (isDemoUser) {
			GTMAnalytics.ageConfirmed();
		}
	}, [isDemoUser, modalConfig, switchSafeMode]);

	const handleCancel = useCallback(() => {
		// setShowCancelButton(true);
		modalConfig.reject?.();
		setModalConfig({} as TAgeConfirmationConfig);
	}, [modalConfig]);

	return (
		<AgeConfirmationContext.Provider value={{ openAgeConfirmation }}>
			{children}

			<NSFWAgeConfirmation />

			<ConfirmModal
				title={t('modalTitle')}
				info={t('modalText')}
				locked={true}
				hasCancel={showCancelButton}
				onConfirm={handleConfirm}
				onClose={close}
				onCancel={handleCancel}
				open={isOpen}
				confirmText={t('confirmText')}
				cancelText={t('cancelText')}
				align="left"
				transparent={false}
				multiline={true}
				hasClose={false}
			/>
		</AgeConfirmationContext.Provider>
	);
};
